import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { formatPhone, localized } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink } from '../components/RecordLink.js';

/**
 * Sur la route — where the crew is today.
 *
 * The screen that answers "who is out, and how is the day going?", which the old workflow
 * could only answer by reading a grid of booked blocks and inferring the rest. So this is two
 * views of the same day, stacked:
 *
 *   1. **The strip.** One card per person, ordered by how much a dispatcher needs to know
 *      about them — on site first, nothing booked last. This is the part you can read from
 *      across the room.
 *   2. **The grid.** The day laid out per person, for planning and for spotting the gap at
 *      14:00 that a callback could fill.
 *
 * Two deliberate departures from how this is usually drawn:
 *
 * **Visits without a time are not put on the clock.** 560 of the imported visits were
 * promised as "mercredi AM" and nothing finer. They sit in a band above the grid, grouped by
 * the period that was actually promised. Drawing them at 08:00 would invent a commitment and
 * make the technician late by definition.
 *
 * **A visit with nobody on it gets its own column, at the left, where it cannot be missed.**
 * A booking no technician can see is the most expensive thing this screen could hide: a
 * customer is expecting a van that is not coming.
 */

type Person = { id: string; name: string; color: string | null; lead: boolean };

type Visit = {
  id: string;
  number: string | null;
  status: string;
  startsAt: string;
  endsAt: string | null;
  precision: 'exact' | 'period' | 'day';
  period: string | null;
  /** Set when the technician left for the stop. The board's answer to "where is he?". */
  enRouteAt: string | null;
  /** A revised arrival time, sent from the van. Cleared at arrival. */
  etaAt: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  completedAt: string | null;
  outcome: 'complete' | 'partial' | 'return_needed' | null;
  runningSince: string | null;
  /** Time logged against the visit. A record filed afterwards, not an arrival. */
  workedMinutes: number;
  note: string | null;
  job: { id: string; number: string; title: string | null };
  customer: { id: string; name: string };
  address: { line1: string; city: string | null; note: string | null; latitude: number | null; longitude: number | null } | null;
  stageColor: string | null;
  crew: Person[];
  /**
   * Service names arrive as `{fr, en}`, the same shape as a pipeline stage — they are
   * per-tenant catalogue entries, not fixed strings. Typing this as `string` is what put
   * `[object Object]` on every card, twice over when a visit had two services.
   */
  services: Array<{ id: string; name: Record<string, string> }>;
  phones: Array<{ raw: string; normalized: string }>;
  late: boolean;
};

type CrewState = 'on_site' | 'travelling' | 'clocked_in' | 'expected' | 'finished' | 'idle';

type Member = {
  id: string;
  name: string;
  color: string | null;
  phone: string | null;
  role: string;
  state: CrewState;
  since: string | null;
  current: { id: string; customer: { id: string; name: string }; address: Visit['address'] } | null;
  next: { id: string; startsAt: string; customer: { id: string; name: string } } | null;
  counts: { total: number; done: number; late: number; untimed: number };
};

type Board = {
  date: string;
  timezone: string;
  /** False when nothing on this day was reported live from the field. */
  hasLiveSignal: boolean;
  crew: Member[];
  visits: Visit[];
  unassigned: Visit[];
  totals: { visits: number; timed: number; untimed: number; unassigned: number; onSite: number };
};

/** Ordered by how urgently a dispatcher needs to look at the person. */
const STATE_ORDER: Record<CrewState, number> = {
  on_site: 0,
  travelling: 1,
  clocked_in: 2,
  expected: 3,
  finished: 4,
  idle: 5,
};

const STATE_LABEL: Record<CrewState, string> = {
  on_site: 'Sur place',
  travelling: 'En route',
  clocked_in: 'Pointé',
  expected: 'Attendu',
  finished: 'Journée terminée',
  idle: 'Rien au calendrier',
};

const PERIOD_LABEL: Record<string, string> = {
  am: 'Avant-midi',
  pm: 'Après-midi',
  day: 'Journée',
  evening: 'Soirée',
  week: 'Cette semaine',
  night: 'Nuit',
};

/**
 * Height of one hour. Fixed, so a block's height reads as its duration.
 *
 * Set by the shortest real visit rather than by taste: 45 minutes is a common slot here, and
 * at 76px per hour that block is 57px — enough for the customer's name and the time, which is
 * the least a dispatcher can act on. Anything tighter and the name is the thing that gets cut.
 */
const HOUR = 76;

/** Below this, a block shows the name and the time and nothing else. */
const COMPACT_BELOW = 64;

/**
 * One card in the untimed band: three lines — customer, the period promised, the address.
 * Measured, not guessed; a wrong value here clips the last card and reads as a broken layout.
 */
const BAND_CARD = 68;
/** Past this many the band scrolls, rather than pushing the whole day off the screen. */
const BAND_MAX_CARDS = 4;

export function BoardScreen() {
  const search = useSearch({ from: '/board' });
  const navigate = useNavigate();
  const today = localDay(new Date());
  const date = search.date ?? today;

  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }), replace: true });

  const query = useQuery({
    queryKey: ['board', date, search.hideIdle],
    queryFn: () =>
      request<Board>(`/board?date=${date}&includeIdle=${search.hideIdle ? 'false' : 'true'}`),
    placeholderData: (prev) => prev,
    // The board is a live view: a stale one is worse than an empty one, because it shows a
    // technician still on site after they have left.
    refetchInterval: date === today ? 60_000 : false,
  });

  /*
   * A minute tick, only while looking at today. It drives the "since" durations and the now
   * line; without it the elapsed times freeze and quietly lie.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (date !== today) return;
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [date, today]);

  const board = query.data;

  const members = useMemo(
    () =>
      [...(board?.crew ?? [])].sort(
        (a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || a.name.localeCompare(b.name, 'fr'),
      ),
    [board],
  );

  /*
   * The strip and the grid answer different questions, so they get different lists.
   *
   * The strip is the roster: everybody, because "who is free right now" is half of what it is
   * for. But a card each for nine people with nothing booked buries the three who are out, so
   * the free ones collapse to a single line of names underneath.
   */
  const working = members.filter((m) => m.counts.total > 0 || m.state === 'on_site' || m.state === 'clocked_in');
  const free = members.filter((m) => !working.includes(m));

  /*
   * The grid is the day, so it only has columns for people who have one. Seven empty columns
   * push the actual work off the right-hand edge, which is the opposite of the point.
   * Alphabetical, because a grid is read by position and positions should not move.
   *
   * `only` narrows it to one person — what clicking their card does.
   */
  const columns = useMemo(
    () =>
      [...(board?.crew ?? [])]
        .filter((m) => (search.only ? m.id === search.only : m.counts.total > 0))
        .sort((a, b) => a.name.localeCompare(b.name, 'fr')),
    [board, search.only],
  );

  const focused = search.only ? board?.crew.find((m) => m.id === search.only) ?? null : null;

  const timed = board?.visits.filter((v) => v.precision === 'exact') ?? [];
  const untimed = board?.visits.filter((v) => v.precision !== 'exact') ?? [];

  /*
   * The window the grid covers, taken from the work rather than fixed at 07:00–18:00: an
   * emergency call at 04:00 must be visible, and a quiet day should not be mostly empty
   * space. Padded to whole hours and never narrower than a normal working day.
   */
  const [fromHour, toHour] = useMemo(() => {
    let min = 7;
    let max = 18;
    for (const visit of timed) {
      const start = new Date(visit.startsAt);
      const end = visit.endsAt ? new Date(visit.endsAt) : start;
      min = Math.min(min, start.getHours());
      max = Math.max(max, end.getHours() + (end.getMinutes() > 0 ? 1 : 0));
    }
    return [Math.max(0, min), Math.min(24, Math.max(max, min + 6))];
  }, [timed]);

  const hours = Array.from({ length: toHour - fromHour }, (_, i) => fromHour + i);
  const gridHeight = hours.length * HOUR;

  /*
   * Every column's untimed band must be exactly the same height, including the gutter's.
   *
   * Otherwise each track starts at a different offset and the hour rules stop meaning the
   * same thing from one column to the next — the one mistake a schedule grid cannot make. So
   * the tallest band sets the height for all of them, and a column with more than a few
   * untimed visits scrolls inside its band instead of dragging the day down.
   */
  const bandHeight = useMemo(() => {
    if (untimed.length === 0) return 0;
    const perColumn = new Map<string, number>();
    for (const visit of untimed) {
      for (const person of visit.crew) perColumn.set(person.id, (perColumn.get(person.id) ?? 0) + 1);
    }
    const unassignedCount = untimed.filter((v) => v.crew.length === 0).length;
    const most = Math.max(unassignedCount, 0, ...perColumn.values());
    return Math.min(most, BAND_MAX_CARDS) * BAND_CARD + 8;
  }, [untimed]);

  const dayStart = useMemo(() => {
    const d = new Date(`${date}T00:00:00`);
    d.setHours(fromHour, 0, 0, 0);
    return d.getTime();
  }, [date, fromHour]);

  const offsetOf = (iso: string) => ((new Date(iso).getTime() - dayStart) / 3_600_000) * HOUR;

  const showNowLine = date === today && now >= dayStart && now <= dayStart + gridHeight * (3_600_000 / HOUR);

  return (
    <div className="screen board">
      <header className="board__head">
        <div className="board__title">
          <h1>Sur la route</h1>
          {board && (
            <p className="board__summary">
              {board.totals.onSite > 0 ? (
                <strong>{board.totals.onSite} sur place</strong>
              ) : (
                <span>Personne sur place</span>
              )}
              <span aria-hidden="true"> · </span>
              <span>
                {board.totals.visits} visite{board.totals.visits === 1 ? '' : 's'}
              </span>
              {board.totals.untimed > 0 && (
                <>
                  <span aria-hidden="true"> · </span>
                  <span>{board.totals.untimed} sans heure fixe</span>
                </>
              )}
              {board.totals.unassigned > 0 && (
                <>
                  <span aria-hidden="true"> · </span>
                  <strong className="board__alert">{board.totals.unassigned} à assigner</strong>
                </>
              )}
            </p>
          )}
        </div>

        <div className="board__dateNav">
          <button type="button" className="btn btn--ghost" onClick={() => setSearch({ date: shiftDay(date, -1) })} aria-label="Jour précédent">
            ‹
          </button>
          <div className="board__date">
            <span className="board__dateLabel">{longDay(date)}</span>
            {date !== today && (
              <button type="button" className="board__todayLink" onClick={() => setSearch({ date: undefined })}>
                Revenir à aujourd’hui
              </button>
            )}
          </div>
          <button type="button" className="btn btn--ghost" onClick={() => setSearch({ date: shiftDay(date, 1) })} aria-label="Jour suivant">
            ›
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setSearch({ date: e.target.value || undefined })}
            className="board__datePicker"
            aria-label="Choisir une date"
          />
        </div>

        {focused && (
          <button type="button" className="board__clearFocus" onClick={() => setSearch({ only: undefined })}>
            ← Toute l’équipe
          </button>
        )}

        <label className="board__toggle">
          <input
            type="checkbox"
            checked={Boolean(search.hideIdle)}
            onChange={(e) => setSearch({ hideIdle: e.target.checked ? true : undefined })}
          />
          Masquer les personnes sans visite
        </label>
      </header>

      {query.isLoading && !board && <p className="screen__loading">Chargement…</p>}

      {board && (
        <>
          {/* ------------------------------------------------------- the strip */}
          <section className="crewStrip" aria-label="État de l’équipe">
            {members.length === 0 && (
              <p className="section__empty">Aucune personne active dans cette équipe.</p>
            )}
            {working.map((member) => (
              <article
                key={member.id}
                className={`crewCard crewCard--${member.state}`}
                style={{ '--crew-color': member.color ?? '#64748B' } as React.CSSProperties}
              >
                <header className="crewCard__head">
                  <span className="crewCard__dot" aria-hidden="true" />
                  {/*
                    * The whole card is clickable, via a link that stretches over it rather
                    * than by wrapping the card in an anchor — the card contains its own links
                    * (the customer, the phone) and nesting anchors is invalid HTML that
                    * browsers recover from unpredictably. The links inside sit above this one.
                    */}
                  <button
                    type="button"
                    className="crewCard__name crewCard__open"
                    onClick={() => setSearch({ only: member.id })}
                    aria-label={`Voir la journée de ${member.name}`}
                  >
                    {member.name}
                  </button>
                  {member.counts.late > 0 && (
                    <span className="crewCard__late" title="Visite dont l’heure est passée sans arrivée">
                      {member.counts.late} en retard
                    </span>
                  )}
                </header>

                <span className={`crewCard__state crewCard__state--${member.state}`}>
                  {STATE_LABEL[member.state]}
                  {member.since && <span className="crewCard__since"> · {elapsed(member.since, now)}</span>}
                </span>

                {member.current ? (
                  <div className="crewCard__where">
                    <RecordLink kind="customer" id={member.current.customer.id} className="crewCard__customer">
                      {member.current.customer.name}
                    </RecordLink>
                    {member.current.address && (
                      <span className="crewCard__address">
                        {member.current.address.line1}
                        {member.current.address.city ? `, ${member.current.address.city}` : ''}
                      </span>
                    )}
                  </div>
                ) : member.next ? (
                  <div className="crewCard__where">
                    <span className="crewCard__nextLabel">Prochain arrêt</span>
                    <span className="crewCard__nextTime">
                      {clock(member.next.startsAt)} · {member.next.customer.name}
                    </span>
                  </div>
                ) : (
                  <div className="crewCard__where crewCard__where--empty">
                    {member.counts.total === 0 ? 'Aucune visite aujourd’hui' : 'Plus rien de prévu'}
                  </div>
                )}

                <footer className="crewCard__foot">
                  {member.counts.total > 0 && (
                    <span className="crewCard__progress tnum">
                      {member.counts.done}/{member.counts.total} faites
                    </span>
                  )}
                  {member.phone && (
                    <a className="crewCard__phone phone" href={`tel:${member.phone.replace(/\D/g, '')}`}>
                      {formatPhone(member.phone)}
                    </a>
                  )}
                </footer>
              </article>
            ))}
          </section>

          {/* Everyone with nothing booked, in one line. Present — a dispatcher looking for
              somebody to send needs these names — without costing a card each. */}
          {free.length > 0 && (
            <p className="crewFree">
              <span className="crewFree__label">Libres</span>
              {free.map((member) => (
                <span key={member.id} className="crewFree__name">
                  <span
                    className="crewFree__dot"
                    style={{ background: member.color ?? '#64748B' }}
                    aria-hidden="true"
                  />
                  {member.name}
                </span>
              ))}
            </p>
          )}

          {/*
            * Said out loud rather than left to be inferred. On an imported day every person
            * reads "Attendu", which looks like a broken screen unless the reason is on the
            * page: nothing was ever reported from the road, because there was nothing to
            * report it with.
            */}
          {!board.hasLiveSignal && board.totals.visits > 0 && (
            <p className="board__notice">
              Aucune position en direct pour cette journée. Les arrivées et les départs
              apparaîtront ici dès que l’équipe utilisera l’application mobile ; l’historique
              montre ce qui était prévu et le temps qui a été inscrit.
            </p>
          )}

          {/* ------------------------------------------------ untimed + grid */}
          <section className="board__grid" aria-label="Horaire de la journée">
            <div className="board__gutter">
              <div className="board__gutterHead" />
              {bandHeight > 0 && (
                <div className="board__gutterBand" style={{ height: bandHeight }}>
                  Sans heure fixe
                </div>
              )}
              <div className="board__hours">
                {hours.map((hour) => (
                  <div key={hour} className="board__hour" style={{ height: HOUR }}>
                    <span className="board__hourLabel tnum">{String(hour).padStart(2, '0')}:00</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="board__columns">
              {board.unassigned.length > 0 && (
                <Column
                  key="__unassigned"
                  title="À assigner"
                  danger
                  color="#B45309"
                  visits={board.unassigned}
                  bandHeight={bandHeight}
                  hours={hours}
                  offsetOf={offsetOf}
                  gridHeight={gridHeight}
                  now={now}
                  showNowLine={showNowLine}
                />
              )}

              {columns.map((member) => (
                <Column
                  key={member.id}
                  title={member.name}
                  subtitle={`${member.counts.done}/${member.counts.total}`}
                  color={member.color ?? '#64748B'}
                  visits={board.visits.filter((v) => v.crew.some((p) => p.id === member.id))}
                  bandHeight={bandHeight}
                  hours={hours}
                  offsetOf={offsetOf}
                  gridHeight={gridHeight}
                  now={now}
                  showNowLine={showNowLine}
                />
              ))}

              {columns.length === 0 && board.unassigned.length === 0 && (
                <div className="empty">
                  <span className="empty__title">Rien au calendrier ce jour-là</span>
                  <p className="empty__body">Choisissez une autre date, ou planifiez une visite.</p>
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

/**
 * One person's day.
 *
 * A visit shared by a crew is rendered in each of their columns, marked with the crew size,
 * because "is Sebouh on this one too?" is the question a dispatcher asks before moving it.
 */
function Column(props: {
  title: string;
  subtitle?: string;
  color: string;
  danger?: boolean;
  visits: Visit[];
  /** Shared across every column so the tracks below stay aligned. 0 when the day has none. */
  bandHeight: number;
  hours: number[];
  offsetOf: (iso: string) => number;
  gridHeight: number;
  now: number;
  showNowLine: boolean;
}) {
  const { visits, offsetOf, gridHeight, hours, now, showNowLine } = props;
  const timed = visits.filter((v) => v.precision === 'exact');
  const untimed = visits.filter((v) => v.precision !== 'exact');
  const lanes = useMemo(() => layout(timed), [timed]);

  return (
    <div
      className={`bcol${props.danger ? ' bcol--danger' : ''}`}
      style={{ '--col-color': props.color } as React.CSSProperties}
    >
      <header className="bcol__head">
        <span className="bcol__swatch" aria-hidden="true" />
        <span className="bcol__name">{props.title}</span>
        {props.subtitle && <span className="bcol__count tnum">{props.subtitle}</span>}
      </header>

      {/* The band for visits that were never given a time, labelled with the promise that was
          actually made — so "AM" and "toute la journée" do not look like the same thing. */}
      {props.bandHeight > 0 && (
        <div className="bcol__band" style={{ height: props.bandHeight }}>
          {untimed.length === 0 ? (
            <span className="bcol__bandEmpty" aria-hidden="true" />
          ) : (
            untimed.map((visit) => (
              <VisitCard key={visit.id} visit={visit} variant="band" compact={false} now={now} />
            ))
          )}
        </div>
      )}

      <div className="bcol__track" style={{ height: gridHeight }}>
        {hours.map((hour, i) => (
          <div key={hour} className="bcol__rule" style={{ top: i * HOUR }} aria-hidden="true" />
        ))}

        {showNowLine && (
          <div
            className="bcol__now"
            style={{ top: offsetOf(new Date(now).toISOString()) }}
            aria-hidden="true"
          />
        )}

        {timed.map((visit) => {
          const lane = lanes.get(visit.id)!;
          const top = offsetOf(visit.startsAt);
          const end = visit.endsAt ? offsetOf(visit.endsAt) : top + HOUR;
          return (
            <div
              key={visit.id}
              className="bcol__slot"
              style={{
                top,
                // A minimum so a half-hour call is still clickable and readable.
                height: Math.max(end - top, 30),
                left: `${(lane.index / lane.of) * 100}%`,
                width: `${(1 / lane.of) * 100}%`,
              }}
            >
              <VisitCard
                visit={visit}
                variant="timed"
                compact={Math.max(end - top, 30) < COMPACT_BELOW}
                now={now}
              />
            </div>
          );
        })}

        {timed.length === 0 && untimed.length === 0 && (
          <span className="bcol__free">Libre</span>
        )}
      </div>
    </div>
  );
}

/**
 * One visit.
 *
 * What goes on the card is what someone needs to act: who, where, and what state it is in.
 * The service list and the note are what a dispatcher reads out over the phone, so they are
 * on the card rather than a click away.
 */
function VisitCard({
  visit,
  variant,
  compact,
  now,
}: {
  visit: Visit;
  variant: 'timed' | 'band';
  /** The block is too short for anything past the name and the time. */
  compact: boolean;
  now: number;
}) {
  /*
   * Where the technician is, derived from the timestamps rather than from the status.
   *
   * En route, delayed and arrived are not statuses — the visit is `in_progress` from the
   * moment somebody leaves — so the board reads the same three timestamps the field client
   * writes. `not_done` is its own status and outranks all of it: a visit that failed is not a
   * visit somebody is still driving to.
   */
  const state = visit.status === 'not_done' || visit.status === 'no_show'
    ? 'failed'
    : visit.completedAt || visit.status === 'completed'
      ? 'done'
      : visit.arrivedAt && !visit.departedAt
        ? 'onsite'
        : visit.departedAt
          ? 'left'
          : visit.etaAt
            ? 'delayed'
            : visit.enRouteAt
              ? 'enroute'
              : visit.late
                ? 'late'
                : 'planned';

  const when =
    variant === 'band'
      ? PERIOD_LABEL[visit.period ?? 'day'] ?? 'Sans heure'
      : `${clock(visit.startsAt)}${visit.endsAt ? `–${clock(visit.endsAt)}` : ''}`;

  return (
    <RecordLink
      kind="job"
      id={visit.job.id}
      className={`visit visit--${variant} visit--${state}${compact ? ' visit--compact' : ''}`}
      /* The whole card as a tooltip, so a compact block still gives up its detail on hover. */
      title={[
        visit.customer.name,
        when,
        visit.address ? `${visit.address.line1}${visit.address.city ? `, ${visit.address.city}` : ''}` : null,
        visit.services.map((s) => localized(s.name, 'fr')).join(' · ') || null,
        visit.crew.length > 1 ? `Équipe : ${visit.crew.map((p) => p.name).join(', ')}` : null,
        visit.workedMinutes > 0 ? `${duration(visit.workedMinutes)} inscrites` : null,
      ]
        .filter(Boolean)
        .join('\n')}
    >
      {/* The customer first. It is the one thing that identifies the stop, so it is never
          the thing that gets clipped. */}
      <span className="visit__customer">{visit.customer.name}</span>

      <span className="visit__when tnum">
        {when}
        {visit.crew.length > 1 && (
          <span className="visit__crew" title={visit.crew.map((p) => p.name).join(', ')}>
            {visit.crew.length}
          </span>
        )}
      </span>

      {!compact && visit.address && (
        <span className="visit__address">
          {visit.address.line1}
          {visit.address.city ? `, ${visit.address.city}` : ''}
        </span>
      )}

      {!compact && variant !== 'band' && visit.services.length > 0 && (
        <span className="visit__services">
          {visit.services.map((s) => localized(s.name, 'fr')).join(' · ')}
        </span>
      )}

      {!compact && state === 'enroute' && visit.enRouteAt && (
        <span className="visit__badge visit__badge--enroute">En route depuis {elapsed(visit.enRouteAt, now)}</span>
      )}
      {!compact && state === 'delayed' && visit.etaAt && (
        <span className="visit__badge visit__badge--delayed">En retard — arrivée {clock(visit.etaAt)}</span>
      )}
      {!compact && state === 'failed' && (
        <span className="visit__badge visit__badge--failed">Travail non fait</span>
      )}
      {!compact && state === 'onsite' && visit.arrivedAt && (
        <span className="visit__badge visit__badge--onsite">Sur place depuis {elapsed(visit.arrivedAt, now)}</span>
      )}
      {!compact && state === 'late' && (
        <span className="visit__badge visit__badge--late">Heure passée, pas d’arrivée</span>
      )}
      {!compact && state === 'done' && visit.workedMinutes > 0 && (
        <span className="visit__badge visit__badge--done">{duration(visit.workedMinutes)} inscrites</span>
      )}
    </RecordLink>
  );
}

/**
 * Side-by-side placement for visits that overlap in one column.
 *
 * Double-booking happens, and hiding it behind an overlap would hide the problem. Visits are
 * grouped into clusters of mutual overlap, and each cluster is split into as many lanes as it
 * needs, so a clash is visible as two narrow blocks rather than one covering the other.
 */
function layout(visits: Visit[]): Map<string, { index: number; of: number }> {
  const out = new Map<string, { index: number; of: number }>();
  const sorted = [...visits].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const endOf = (v: Visit) => new Date(v.endsAt ?? v.startsAt).getTime();

  let cluster: Visit[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (cluster.length === 0) return;
    const laneEnds: number[] = [];
    const placed: Array<[string, number]> = [];
    for (const visit of cluster) {
      const start = new Date(visit.startsAt).getTime();
      let lane = laneEnds.findIndex((end) => end <= start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(0);
      }
      laneEnds[lane] = endOf(visit);
      placed.push([visit.id, lane]);
    }
    for (const [id, lane] of placed) out.set(id, { index: lane, of: laneEnds.length });
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const visit of sorted) {
    const start = new Date(visit.startsAt).getTime();
    if (start >= clusterEnd) flush();
    cluster.push(visit);
    clusterEnd = Math.max(clusterEnd, endOf(visit));
  }
  flush();

  return out;
}

/** `YYYY-MM-DD` in the viewer's own timezone, so "today" means the day they are living in. */
function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function shiftDay(date: string, by: number): string {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + by);
  return localDay(d);
}

function longDay(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('fr-CA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
}

/** A logged duration, in the form a person would say it. */
function duration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')}`;
}

/** Coarse on purpose: "3 h 20" is what someone says out loud, "3 h 20 min 12 s" is not. */
function elapsed(iso: string, now: number): string {
  const minutes = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')}`;
}
